import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import { hasExplorerOnboarding } from "../explorer/hasExplorerOnboarding";
import { useGetExplorerMessagingLimit } from "../api/queries/useGetExplorerMessagingLimit";

/** Explorer-only: max 5 messages sent per week across all conversations. */
export function useExplorerMessagingLimit() {
  const { objectId, user } = useAppContext();
  const userId = objectId ?? "";
  const hasPaid = user?.user?.hasPaid === 1;

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isLoggedIn() && !!objectId && !hasPaid,
    retry: false,
    staleTime: 60_000,
  });

  const isExplorer = useMemo(
    () => !hasPaid && hasExplorerOnboarding(explorerMe),
    [hasPaid, explorerMe]
  );

  const { data: limitData, refetch: refetchLimit } = useGetExplorerMessagingLimit({
    userId,
    enabled: isExplorer,
  });

  const canSendMessage = useMemo(() => {
    if (!isExplorer) return true;
    return limitData?.canSendMessage !== false;
  }, [isExplorer, limitData?.canSendMessage]);

  return {
    isExplorer,
    canSendMessage,
    used: limitData?.used ?? 0,
    limit: limitData?.limit ?? 5,
    remaining: limitData?.remaining ?? 5,
    weekResetsUtc: limitData?.weekResetsUtc,
    refetchLimit,
  };
}
